import { useState } from 'react';

import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconExclamationTriangleFilled } from '@iwoca/icons';
import {
    fetchGetSelectPaymentProcessorByStateKey,
    GetContextResponse
} from '@iwoca/lapi-client/edge';
import { Modal, Spinner } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { useQuery } from '@tanstack/react-query';
import { Card, H2, Text } from '@ui';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import messages from './PaymentDetails.messages';
import { getApplicationStatus } from '../../../api/lendingAPI/ApplicationSelector';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import {
    INITIALISE_PAYMENT_METHOD_DEBIT_CARD,
    REFETCH_PAYMENT_METHOD
} from '../../modals/addPaymentMethodModal/AddPaymentMethodModal.actions';
import LinkBankAccount from '../../modals/addPaymentMethodModal/linkBankAccount/LinkBankAccount';
import LinkDebitCard from '../../modals/addPaymentMethodModal/linkDebitCard/LinkDebitCard';
import { UPDATE_BANK_ACCOUNT } from '../../modals/editPaymentMethodModal/EditPaymentMethodModal.actions';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import CollapsibleSection from '../../ui/CollapsibleSection/CollapsibleSection';
import { BankAccountCard } from '../cards/BankAccountCard';
import { CreateNewCard } from '../cards/CreateNewCard';
import PaymentCard from '../cards/PaymentCard';
import RepaymentCard from '../cards/RepaymentCard';
import { CheckoutLinkDebitCard } from '../CheckoutLinkDebitCard/CheckoutLinkDebitCard';
import profileMessages from '../Profile.messages';
import { deserializePaymentMethod } from '../ProfileHelper';

const mapStateToProps = (state: RootState) => ({
    ...state.profile,
    hasDecision: getApplicationStatus(state) !== 'open'
});

type TPaymentMethod = ReturnType<typeof deserializePaymentMethod>;

const mapDispatchToProps = (dispatch: Dispatch) => ({
    showAddDebitCardFormCallback: () =>
        dispatch({
            type: INITIALISE_PAYMENT_METHOD_DEBIT_CARD
        }),
    updateBankAccount: () =>
        dispatch({
            type: UPDATE_BANK_ACCOUNT
        }),
    refetchPaymentMethod: () =>
        dispatch({
            type: REFETCH_PAYMENT_METHOD
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const isBlockedIwocaPaySeller = (
    context: GetContextResponse['data']
): boolean => {
    /*
        iwocaPay Sellers are prevented from updating their bank details.
        It must go through ops
    */
    if (context?.groups?.includes('bank_details_can_modify')) return false;

    //@ts-expect-error schema not updated
    return !!context?.is_iwocapay_seller;
};

const PaymentDetails = ({
    // @ts-expect-error
    paymentMethods,
    showAddBankAccountForm,
    showAddDebitCardForm,
    showAddDebitCardFormCallback,
    hasDecision,
    updateBankAccount,
    refetchPaymentMethod
}: PropsFromRedux) => {
    const paymentCards = paymentMethods.filter(
        (paymentMethod: TPaymentMethod) => paymentMethod.type === 'card'
    );

    const bankAccount = paymentMethods.find(
        (paymentMethod: TPaymentMethod) => paymentMethod.type === 'bank_account'
    );

    return (
        <div className="space-y-m">
            <div>
                <H2>
                    <FormattedMessage
                        {...profileMessages.paymentDetailsTitle}
                    />
                </H2>
                <Text className="mt-xs">
                    <FormattedMessage
                        {...profileMessages.paymentDetailsSubtitle}
                    />
                </Text>
                {getCountry() === 'DE' && (
                    <Text className="mt-xs">
                        <FormattedMessage
                            {...profileMessages.paymentDetailsAnotherSubtitle}
                        />
                    </Text>
                )}
            </div>
            <div className="block grid-cols-2 gap-l min-sm:grid min-l:grid-cols-3">
                <RepaymentAccountDetails
                    showAddBankAccountForm={showAddBankAccountForm}
                    bankAccount={bankAccount}
                    updateBankAccount={updateBankAccount}
                />
            </div>
            <section className="block grid-cols-2 gap-l space-y-m min-sm:grid min-sm:space-y-0 min-l:grid-cols-3">
                {paymentCards.map((paymentMethod: TPaymentMethod) => (
                    <PaymentCard
                        {...paymentMethod}
                        key={paymentMethod.paymentMethodID}
                    />
                ))}
                <AddNewPaymentMethod
                    showAddBankAccountForm={showAddBankAccountForm}
                    showAddDebitCardForm={showAddDebitCardForm}
                    handleClick={showAddDebitCardFormCallback}
                />
                <NewBankAccountForm
                    hasDecision={hasDecision}
                    showAddBankAccountForm={showAddBankAccountForm}
                />
                <DEOnly>
                    <AddNewDirecDebitMandate />
                </DEOnly>
            </section>

            <NewDebitCardForm
                showAddDebitCardForm={showAddDebitCardForm}
                refetchPaymentMethod={refetchPaymentMethod}
            />
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(PaymentDetails);

const RepaymentAccountDetails = ({
    showAddBankAccountForm,
    bankAccount,
    updateBankAccount
}: {
    showAddBankAccountForm: boolean;
    bankAccount: TPaymentMethod;
    updateBankAccount: () => void;
}) => {
    if (showAddBankAccountForm) return null;

    return (
        <>
            <BankAccountCard
                {...bankAccount}
                updateBankAccount={() => updateBankAccount()}
            />
            <RepaymentCard />
        </>
    );
};

const NewBankAccountForm = ({
    hasDecision,
    showAddBankAccountForm
}: {
    hasDecision: boolean;
    showAddBankAccountForm: boolean;
}) => {
    const { data } = useGetContext();
    if (getCountry() === 'DE' && !hasDecision) return null;

    if (!showAddBankAccountForm) return null;

    if (isBlockedIwocaPaySeller(data)) {
        return (
            <Card>
                <p>Please contact your account manager to add bank details.</p>
            </Card>
        );
    }

    return (
        <div className="col-span-2">
            <CollapsibleSection
                title={<FormattedMessage {...messages.addBankAccount} />}
                isCollapsible={false}
            >
                <LinkBankAccount showTitle={false} />
            </CollapsibleSection>
        </div>
    );
};

const NewDebitCardForm = ({
    showAddDebitCardForm,
    refetchPaymentMethod
}: {
    showAddDebitCardForm: boolean;
    refetchPaymentMethod: () => void;
}) => {
    const stateKey = useStateKey();
    const {
        data: paymentProcessor,
        isLoading,
        isError
    } = useQuery({
        queryKey: ['fetchGetSelectPaymentProcessor'],
        queryFn: async () => {
            try {
                return await fetchGetSelectPaymentProcessorByStateKey({
                    stateKey
                });
            } catch (error) {
                if (error instanceof Error) {
                    throw new Error(
                        `Error fetching payment processor: ${error.message}`
                    );
                } else {
                    throw new Error(
                        'Error fetching payment processor: Unknown error'
                    );
                }
            }
        },
        select: (data) => data?.data.payment_processor
    });

    if (getCountry() === 'DE') return null;

    if (!showAddDebitCardForm) return null;

    if (isLoading) return <Spinner />;

    if (isError) return <FallbackErrorModal />;

    // Successful linking of a a debit card with chekcout.com happens asynchronously, which can take a few seconds.
    const refetchPaymentMethodWithPolling = () => {
        let attempts = 0;

        const intervalId = setInterval(() => {
            if (attempts >= 5) {
                clearInterval(intervalId);
                return;
            }

            refetchPaymentMethod();
            attempts++;
        }, 2000);
    };

    return (
        <CollapsibleSection
            title={<FormattedMessage {...messages.addDebitCard} />}
            isCollapsible={false}
        >
            {paymentProcessor === 'Checkout' ? (
                <CheckoutLinkDebitCard
                    onSuccess={refetchPaymentMethodWithPolling}
                />
            ) : (
                <LinkDebitCard
                    showTitle={false}
                    showSubText={false}
                    showDefaultPaymentMethodCheckbox={true}
                />
            )}
        </CollapsibleSection>
    );
};

const AddNewPaymentMethod = ({
    showAddBankAccountForm,
    showAddDebitCardForm,
    handleClick
}: {
    showAddBankAccountForm: boolean;
    showAddDebitCardForm: boolean;
    handleClick: () => void;
}) => {
    if (getCountry() === 'DE' || showAddBankAccountForm || showAddDebitCardForm)
        return null;

    return (
        <CreateNewCard
            label={<FormattedMessage {...messages.addDebitCard} />}
            onClick={handleClick}
        />
    );
};

const AddNewDirecDebitMandate = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    return (
        <>
            <CreateNewCard
                label={<FormattedMessage {...messages.addDirectDebitMandate} />}
                onClick={() => {
                    setIsModalOpen((prevState) => !prevState);
                    tracking.ampli.addNewDirectDebitMandateClicked();
                }}
            />
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen((prevState) => !prevState)}
                closeIcon
            >
                <div className="auto text-center">
                    <IconExclamationTriangleFilled className="size-2xl fill-secondary-40" />
                    <h3>
                        <FormattedMessage {...messages.directDebitModalTitle} />
                    </h3>
                    <p className="text-m font-reg">
                        <FormattedMessage
                            {...messages.directDebitModalDescription}
                            values={{
                                phone: (
                                    <a href="tel:069 94323020">
                                        069 / 94323020
                                    </a>
                                ),
                                email: (
                                    <a href="mailto:support@iwoca.de">
                                        support@iwoca.de
                                    </a>
                                )
                            }}
                        />
                    </p>
                </div>
            </Modal>
        </>
    );
};
